import React, { useState } from 'react';
import NavHeader from '../components/NavHeader';
import FooterSection from '../components/FooterSection';
import { GlassCard } from '../components/shared/GlassCard';
import { GradientText } from '../components/shared/GradientText';

const faqs = [
  {
    question: 'What is HashMedora?',
    answer:
      'HashMedora is a decentralized number lottery platform built on the BSC blockchain. Players select numbers across five different games, and winners are determined by verifiable on-chain randomness.',
  },
  {
    question: 'How are winners chosen?',
    answer:
      "Winners are selected using blockchain-verifiable random number generation. Each draw produces a winning number, and both direct hits and neighboring numbers within the game's neighbor range win prizes.",
  },
  {
    question: 'What is the neighbor network?',
    answer:
      'Each game has a "neighbor" range around the winning number. If your number is close to the winning number (within the neighbor count), you still win a proximity prize. This creates a ~30% total win probability across most games.',
  },
  {
    question: 'How do payouts work?',
    answer:
      'Payouts are made instantly in USDT directly to your connected wallet. No manual claiming required — the smart contract sends your winnings automatically when the draw completes.',
  },
  {
    question: 'What is AutoDraw?',
    answer:
      'AutoDraw allows you to set recurring entries so you never miss a draw. Configure your preferred game, number count, frequency, and stop conditions, and the system will place bets automatically.',
  },
  {
    question: 'Is HashMedora fair?',
    answer:
      'Yes. All draws are executed on-chain using transparent randomness mechanisms. The smart contract code is immutable and verifiable, ensuring no one can manipulate results.',
  },
  {
    question: 'What is the Summit Draw?',
    answer:
      'The Summit Draw is our flagship community event with a 300,000 USDT prize pool. It features three tiers of prizes and 20,503 guaranteed winners. Entry is 5 USD per number.',
  },
  {
    question: 'What wallet do I need?',
    answer:
      'Any BSC-compatible wallet such as MetaMask, Trust Wallet, or WalletConnect-supported wallets. Simply connect your wallet to start playing — no separate registration required.',
  },
];

const steps = [
  { icon: '/images/icon-wallet-connect.svg', title: 'Connect', desc: 'Link your BSC wallet' },
  { icon: '/images/icon-pick-numbers.svg', title: 'Select', desc: 'Pick your lucky numbers' },
  { icon: '/images/icon-ticket.svg', title: 'Bet', desc: 'Place your bet in USDT' },
  { icon: '/images/icon-instant-win.svg', title: 'Win', desc: 'Get instant payout if you win' },
];

function About() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-gradient-to-b from-dark via-dark-secondary to-dark">
      <div
        className="pointer-events-none fixed inset-0 z-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(/images/background-hero.jpg)',
          opacity: 0.14,
        }}
      />
      <div
        className="pointer-events-none fixed inset-0 z-0 bg-gradient-to-b from-dark/80 via-dark-secondary/90 to-dark"
        aria-hidden
      />
      <div
        className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_-10%,rgba(238,212,91,0.08),transparent)]"
        aria-hidden
      />

      <NavHeader />

      <main className="relative z-10 pt-nav pb-20 px-4 sm:px-6 lg:px-8">
        <div className="container-premium max-w-4xl mx-auto">
          {/* Hero */}
          <header className="mb-14 sm:mb-20">
            <GlassCard variant="premium" className="p-8 sm:p-10 lg:p-12 text-center border border-white/5 shadow-[0_24px_80px_rgba(0,0,0,0.45)]">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-hash-gold/90 mb-4">
                Transparency · BSC · USDT
              </p>
              <h1 className="page-title text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                <span className="text-gray-100">About </span>
                <GradientText variant="hash" className="text-3xl sm:text-4xl lg:text-5xl font-bold">
                  Hash
                </GradientText>
                <GradientText variant="medora" className="text-3xl sm:text-4xl lg:text-5xl font-bold">
                  Medora
                </GradientText>
              </h1>
              <p className="text-gray-400 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
                The next generation of decentralized lottery gaming on BSC — fair draws, neighbor wins, and instant
                payouts.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <span className="rounded-full border border-hash-gold/25 bg-hash-gold/5 px-4 py-1.5 text-xs text-gray-300">
                  On-chain randomness
                </span>
                <span className="rounded-full border border-medora-blue/25 bg-medora-blue/5 px-4 py-1.5 text-xs text-gray-300">
                  Five game modes
                </span>
                <span className="rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-gray-300">
                  AutoDraw ready
                </span>
              </div>
            </GlassCard>
          </header>

          {/* What is */}
          <section className="mb-14 sm:mb-16">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px flex-1 max-w-[4rem] bg-gradient-to-r from-transparent to-hash-gold/40" />
              <h2 className="section-title text-xl sm:text-2xl font-bold text-white shrink-0 text-center">
                <GradientText variant="hash" className="text-xl sm:text-2xl font-bold">
                  What
                </GradientText>
                <span className="text-gray-200"> is HashMedora?</span>
              </h2>
              <span className="h-px flex-1 max-w-[4rem] bg-gradient-to-l from-transparent to-hash-gold/40" />
            </div>
            <GlassCard variant="gold" className="p-6 sm:p-8 border border-hash-gold/15">
              <p className="text-gray-300 leading-relaxed mb-4">
                HashMedora is a blockchain-based lottery platform that brings transparency, fairness, and instant
                payouts to number lottery gaming. Built on the Binance Smart Chain (BSC), our platform offers five
                distinct games ranging from entry-level quick wins to elite-tier moonshot prizes.
              </p>
              <p className="text-gray-300 leading-relaxed">
                What sets us apart is our unique &quot;neighbor network&quot; system. Even if you don&apos;t hit the
                exact winning number, numbers close to it still win prizes — creating an industry-leading ~30% total
                win probability across most games.
              </p>
            </GlassCard>
          </section>

          {/* How It Works */}
          <section className="mb-14 sm:mb-16">
            <h2 className="section-title text-xl sm:text-2xl font-bold text-white text-center">
              <GradientText variant="medora" className="text-xl sm:text-2xl font-bold">
                How
              </GradientText>
              <span className="text-gray-200"> it works</span>
            </h2>
            <p className="text-center text-sm text-gray-500 mb-8 max-w-lg mx-auto">
              From wallet to payout in four steps — no custodial accounts.
            </p>
            <div className="relative">
              <div className="absolute left-[8%] right-[8%] top-[2.25rem] hidden h-px bg-gradient-to-r from-hash-gold/20 via-medora-blue/30 to-hash-gold/20 lg:block" />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {steps.map((step, i) => (
                  <GlassCard key={step.title} variant="blue" className="p-5 sm:p-6 text-center border border-white/5">
                    <div className="relative mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-hash-gold/25 bg-gradient-to-br from-hash-gold/15 to-medora-blue/10 shadow-inner">
                      <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-dark text-[10px] font-bold text-hash-gold ring-1 ring-hash-gold/40">
                        {i + 1}
                      </span>
                      <img src={step.icon} alt="" className="h-7 w-7 opacity-95" />
                    </div>
                    <h3 className="text-white font-bold mb-1">{step.title}</h3>
                    <p className="text-gray-500 text-xs leading-snug">{step.desc}</p>
                  </GlassCard>
                ))}
              </div>
            </div>
          </section>

          {/* Fairness */}
          <section className="mb-14 sm:mb-16">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px flex-1 max-w-[3rem] bg-gradient-to-r from-transparent to-medora-blue/35" />
              <h2 className="section-title text-xl sm:text-2xl font-bold text-white shrink-0 text-center">
                <span className="text-gray-200">Fairness & </span>
                <GradientText variant="medora" className="text-xl sm:text-2xl font-bold">
                  transparency
                </GradientText>
              </h2>
              <span className="h-px flex-1 max-w-[3rem] bg-gradient-to-l from-transparent to-medora-blue/35" />
            </div>
            <GlassCard variant="premium" className="p-6 sm:p-8 border border-white/10">
              <p className="text-gray-300 leading-relaxed mb-4">
                All draws on HashMedora are powered by on-chain verifiable randomness. Our smart contracts are immutable
                and publicly auditable. The winning number for each draw is generated through a transparent process that
                cannot be manipulated by anyone — including the team.
              </p>
              <p className="text-gray-300 leading-relaxed">
                All transactions, bets, and payouts are recorded on the BSC blockchain, providing complete transparency.
                Players can verify every aspect of the game mechanics at any time.
              </p>
            </GlassCard>
          </section>

          {/* FAQ */}
          <section className="mb-14 sm:mb-16">
            <h2 className="section-title text-xl sm:text-2xl font-bold text-white text-center">
              Frequently asked{' '}
              <GradientText variant="hash" className="text-xl sm:text-2xl font-bold">
                questions
              </GradientText>
            </h2>
            <p className="text-center text-sm text-gray-500 mb-8">Tap a question to expand the answer.</p>
            <div className="space-y-2 sm:space-y-3">
              {faqs.map((faq, i) => {
                const open = openFaq === i;
                return (
                  <GlassCard
                    key={faq.question}
                    variant="gold"
                    className={`p-0 overflow-hidden border transition-colors duration-200 ${
                      open ? 'border-hash-gold/35 bg-hash-gold/[0.03]' : 'border-hash-gold/10'
                    }`}
                    hover={false}
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="flex w-full items-center justify-between gap-4 p-4 sm:p-5 text-left transition-colors hover:bg-white/[0.02]"
                      aria-expanded={open}
                    >
                      <span className="text-sm font-semibold text-white sm:text-base pr-2">{faq.question}</span>
                      <span
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-hash-gold/25 bg-hash-gold/10 transition-transform duration-200 ${
                          open ? 'rotate-180' : ''
                        }`}
                      >
                        <svg className="h-4 w-4 text-hash-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </button>
                    {open ? (
                      <div className="border-t border-hash-gold/10 px-4 pb-4 pt-0 sm:px-5 sm:pb-5">
                        <p className="pt-4 text-sm leading-relaxed text-gray-300 sm:text-base">{faq.answer}</p>
                      </div>
                    ) : null}
                  </GlassCard>
                );
              })}
            </div>
          </section>

          {/* Responsible Gaming */}
          <GlassCard variant="plain" className="border border-red-500/20 bg-red-500/[0.06] p-6 sm:p-8 text-center">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-500/15 ring-1 ring-red-500/25">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-red-300 mb-2">Responsible gaming</h3>
            <p className="text-gray-400 text-sm max-w-xl mx-auto leading-relaxed">
              HashMedora is intended for entertainment purposes. Please play responsibly and within your means. If you
              feel you may have a gambling problem, please seek help from professional organizations.
            </p>
          </GlassCard>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}

export default About;
